import endpoints from '~/utils/api/endpoints';
import {
	useMutation as _useMutation,
	useQuery as _useQuery,
	useInfiniteQuery as _useInfiniteQuery,
	UseInfiniteQueryResult,
	UseMutationResult,
	UseQueryResult,
	useQueryClient,
} from '@tanstack/react-query';
import {
	get as getMutate,
	put as putMutate,
	post as postMutate,
	del as delMutate,
} from '~/utils/api/requests/mutate';
import {
	get as getQuery,
	put as putQuery,
	post as postQuery,
	del as delQuery,
} from '~/utils/api/requests/query';
import {
	get as getInfiniteQuery,
	put as putInfiniteQuery,
	post as postInfiniteQuery,
	del as delInfiniteQuery,
} from '~/utils/api/requests/infiniteQuery';
import {
	ApiError,
	Endpoint,
	InfiniteQueryConfig,
	MutationConfig,
	QueryConfig,
} from '~/models/request';
import { AxiosError } from 'axios';
import { formatEndpoint } from '~/utils/api/requests';

export const useRevalidate = () => {
	const queryClient = useQueryClient();
	return async (...queries: Endpoint[]) =>
		await Promise.all(
			queries?.map(async (query) => {
				const queryKey = formatEndpoint(query)?.key;
				if (!queryKey) return;
				return await queryClient.invalidateQueries(queryKey);
			})
		);
};

export const get = { mutate: getMutate, query: getQuery, infiniteQuery: getInfiniteQuery };
export const put = { mutate: putMutate, query: putQuery, infiniteQuery: putInfiniteQuery };
export const post = { mutate: postMutate, query: postQuery, infiniteQuery: postInfiniteQuery };
export const del = { mutate: delMutate, query: delQuery, infiniteQuery: delInfiniteQuery };
export const req = endpoints;

type MutationRequest<Data = unknown, Variables = void, Error = void> = (
	endpoint: Endpoint,
	options?: MutationConfig<Data, Error, Variables>
) => UseMutationResult<Data, Error, Variables>;

type QueryRequest<QueryData = unknown, Data = QueryData, Error = void> = (
	endpoint: Endpoint,
	options?: QueryConfig<QueryData, Data, Error>
) => UseQueryResult<Data, Error>;

type InfiniteQueryRequest<QueryData = unknown, Data = QueryData, Error = void> = (
	endpoint: Endpoint,
	options?: InfiniteQueryConfig<QueryData, Data, Error>
) => UseInfiniteQueryResult<Data, Error>;

export const useInfiniteGet = <QueryData = unknown, Data = QueryData, Error = ApiError>(
	endpoint: Endpoint,
	options?: InfiniteQueryConfig<QueryData, Data, AxiosError<Error>>
) => _useInfiniteQuery(getInfiniteQuery(endpoint, options));

export const useGet = <QueryData = unknown, Data = QueryData, Error = ApiError>(
	endpoint: Endpoint,
	options?: QueryConfig<QueryData, Data, AxiosError<Error>>
) => _useQuery(getQuery(endpoint, options));

export const usePost = <Data = unknown, Variables = void, Error = ApiError>(
	endpoint: Endpoint,
	options?: MutationConfig<Data, AxiosError<Error>, Variables>
) => _useMutation(postMutate(endpoint, options));

export const usePut = <Data = unknown, Variables = void, Error = ApiError>(
	endpoint: Endpoint,
	options?: MutationConfig<Data, AxiosError<Error>, Variables>
) => _useMutation(putMutate(endpoint, options));

export const useDelete = <Data = unknown, Variables = void, Error = ApiError>(
	endpoint: Endpoint,
	options?: MutationConfig<Data, AxiosError<Error>, Variables>
) => _useMutation(delMutate(endpoint, options));

export const useMutation = <Data = unknown, Variables = void, Error = AxiosError<ApiError>>(
	fn: (reqs: {
		get: MutationRequest<Data, Variables, Error>;
		put: MutationRequest<Data, Variables, Error>;
		post: MutationRequest<Data, Variables, Error>;
		del: MutationRequest<Data, Variables, Error>;
	}) => UseMutationResult<Data, Error, Variables>
) =>
	fn({
		get: (endpoint, options) => _useMutation(getMutate(endpoint, options)),
		put: (endpoint, options) => _useMutation(putMutate(endpoint, options)),
		post: (endpoint, options) => _useMutation(postMutate(endpoint, options)),
		del: (endpoint, options) => _useMutation(delMutate(endpoint, options)),
	});

export const useQuery = <QueryData = unknown, Data = QueryData, Error = AxiosError<ApiError>>(
	fn: (reqs: {
		get: QueryRequest<QueryData, Data, Error>;
		put: QueryRequest<QueryData, Data, Error>;
		post: QueryRequest<QueryData, Data, Error>;
		del: QueryRequest<QueryData, Data, Error>;
	}) => UseQueryResult<Data, Error>
) =>
	fn({
		get: (endpoint, options) => _useQuery(getQuery(endpoint, options)),
		put: (endpoint, options) => _useQuery(putQuery(endpoint, options)),
		post: (endpoint, options) => _useQuery(postQuery(endpoint, options)),
		del: (endpoint, options) => _useQuery(delQuery(endpoint, options)),
	});

export const useInfiniteQuery = <
	QueryData = unknown,
	Data = QueryData,
	Error = AxiosError<ApiError>
>(
	fn: (reqs: {
		get: InfiniteQueryRequest<QueryData, Data, Error>;
		put: InfiniteQueryRequest<QueryData, Data, Error>;
		post: InfiniteQueryRequest<QueryData, Data, Error>;
		del: InfiniteQueryRequest<QueryData, Data, Error>;
	}) => UseInfiniteQueryResult<Data, Error>
) =>
	fn({
		get: (endpoint, options) => _useInfiniteQuery(getInfiniteQuery(endpoint, options)),
		put: (endpoint, options) => _useInfiniteQuery(putInfiniteQuery(endpoint, options)),
		post: (endpoint, options) => _useInfiniteQuery(postInfiniteQuery(endpoint, options)),
		del: (endpoint, options) => _useInfiniteQuery(delInfiniteQuery(endpoint, options)),
	});

export default endpoints;
